import { api } from './api';

export interface DailyStats {
  date: string;
  sessions: number;
  conversions: number;
  bounce_rate: number;
  avg_time_on_page: number;
}

export interface Session {
  id: string;
  session_id: string;
  language: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  referrer?: string;
  device_type?: string;
  converted: number;
  conversion_type?: string;
  started_at: string;
  posthog_session_id?: string | null;
  // "Das waren wir": true, wenn im selben Browser ein Agency-OS-Login gefunden wurde
  // (leitkraft.de und leitkraft.de/app teilen sich localStorage, siehe ConsentBanner.tsx
  // Gegenstueck im Marketing-Repo).
  is_internal?: number;
  // "Das waren echte Leads": nur gesetzt, wenn utm_content auf eine personalisierte
  // Fraud-Seite (lead_seiten) passt -- siehe api/routers/lp_analytics.py::lp_sessions.
  lead_id?: number | null;
  lead_company?: string | null;
  events?: Array<{ event_type: string; event_data?: Record<string, unknown>; created_at: string }>;
}

// Der Praefix bis vor die Session-ID, so wie er sich aus der eigenen PostHog-Cloud-Projekt-URL
// ergibt (z. B. "https://eu.posthog.com/project/12345/replay") -- 1:1 aus dem Browser kopiert,
// sobald das Projekt existiert (siehe Teil B). Kein geratenes URL-Schema hier: PostHog haengt die
// Projekt-ID mit ein, die es vor der Kontoerstellung noch nicht gibt. Ohne Wert bleibt der
// Recording-Link aus.
const POSTHOG_REPLAY_URL_PREFIX = import.meta.env.VITE_POSTHOG_REPLAY_URL_PREFIX as string | undefined;

export function posthogRecordingUrl(posthogSessionId: string): string | null {
  return POSTHOG_REPLAY_URL_PREFIX ? `${POSTHOG_REPLAY_URL_PREFIX}/${posthogSessionId}` : null;
}

export interface ABVariant {
  id: string;
  variant_id: string;
  name: string;
  element: string;
  content: string;
  language: string;
  status: 'active' | 'inactive';
  impressions: number;
  conversions: number;
  conversion_rate: number;
  thompson_probability?: number;
  created_at: string;
}

export interface BridgeRow {
  niche: string;
  sessions: number;
  conversions: number;
  conversion_rate: number;
  top_rewrite_id?: string | number;
}

export interface CreateVariantInput {
  variant_id: string;
  name: string;
  element: string;
  content: string;
  language?: string;
}

export interface PostHogStats {
  available: boolean;
  pageviews: number;
  sessions: number;
  top_sources: Array<{ source: string; sessions: number }>;
  browsers: Array<{ browser: string; sessions: number }>;
  os: Array<{ os: string; sessions: number }>;
}

export interface PostHogLive {
  available: boolean;
  active_now: number;
}

export type SiteFilter = 'all' | 'leitkraft' | 'leitscope';

export interface StatsResponse {
  daily: DailyStats[];
  totals: {
    sessions: number;
    conversions: number;
    bounce_rate: number;
    avg_time_on_page: number;
    traffic_sources: Record<string, number>;
    device_split: Record<string, number>;
    // 'unbekannt' = Sessions vor der Site-Trennung, ohne rueckwirkende Zuordnung
    site_split: Record<string, number>;
    page_split: Record<string, number>;
  };
}

export interface SessionsResponse {
  sessions: Session[];
  total: number;
  page: number;
}

export interface LPInsightEvidenceSection {
  abschnitt: string;
  reach: number;
  reach_pct: number;
  dropoff_count: number;
  dropoff_pct_of_non_converted: number;
}

export interface LPInsight {
  id: number;
  generated_at: string;
  period_start: string;
  period_end: string;
  site: SiteFilter | null;
  sessions_analyzed: number;
  headline: string;
  diagnosis: string;
  suggestion: string;
  evidence_json: string;
  status: 'new' | 'dismissed';
}

export interface FraudCalcValues {
  company: string;
  clicks: number;
  cpc: number;
  bot_rate: number;
  spend: number;
  loss: number;
  yearly: number;
  bot_clicks: number;
  branch: string;
}

export interface BookingFormFields {
  company?: string;
  url?: string;
  revenue?: string;
  goal?: string;
}

export interface LPBooking {
  id: number;
  name: string;
  email: string;
  phone: string;
  date: string;
  time: string;
  duration: number;
  status: string;
  lang: string;
  notes: string | null;
  // 'fraud_calc' (Rechner-Beleg) / 'fraud_audit' (Termin-Anfrage) -- beide leitscope.com.
  // 'waitlist' bzw. Legacy-NULL -- leitkraft.de-Warteliste.
  kind: string | null;
  calc_json: string | null;
  form_json: string | null;
  // Verknuepft mit lp_sessions.session_id, wenn das Formular sie mitgeschickt hat
  // (siehe lib/session.ts im Marketing-Repo) -- fuer den vereinten Besucher-Tab.
  session_id: string | null;
  created_at: string;
}

export interface ABTestsResponse {
  variants: ABVariant[];
}

// Kanal und Zielseiten kommen vom Server (services/utm_kanaele.py) und werden hier bewusst
// NICHT nachgebaut -- zwei Listen laufen sonst auseinander, und der Unterschied faellt erst
// in der Auswertung auf, wenn 'linkedin' und 'LinkedIn' zwei Zeilen sind.
export interface UtmKanal {
  key: string;
  source: string;
  medium: string;
  kuerzel: string;
  label: string;
}

export interface UtmZiel {
  url: string;
  label: string;
}

/** Eine Veroeffentlichung samt fertigem Link. `code` steht als utm_content darin. */
export interface LPLink {
  id: number;
  code: string;
  kanal: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  ziel_url: string;
  titel: string;
  notiz: string | null;
  veroeffentlicht_at: string | null;
  /** Bleibt leer bis zur spaeteren Kurz-Link-Stufe. */
  kurz_code: string | null;
  erstellt_von: number | null;
  erstellt_at: string;
  /** Vom Server gebaut, nicht gespeichert. */
  link: string;
  /** Besuche ueber diesen Link, ohne Bots und ohne Team-Besuche. */
  besuche: number;
  anmeldungen: number;
  /** Kurzform zum Verschicken, null bei Eintraegen von vor der Kurz-Link-Stufe. */
  kurz_link: string | null;
  /** Aufrufe des Kurz-Links, vom Server gezaehlt · unabhaengig von der Cookie-Einwilligung. */
  klicks: number;
}

export interface CreateLinkInput {
  kanal: string;
  ziel_url: string;
  utm_campaign: string;
  titel: string;
  notiz?: string;
  veroeffentlicht_at?: string;
}

export const lpAnalyticsApi = {
  getStats: (start: string, end: string) =>
    api.get<StatsResponse>(`/lp-analytics/stats?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`),

  getSessions: (page: number, site: SiteFilter = 'all') =>
    api.get<SessionsResponse>(
      `/lp-analytics/sessions?page=${page}${site !== 'all' ? `&site=${site}` : ''}`
    ),

  getABTests: () =>
    api.get<ABTestsResponse>('/lp-analytics/ab'),

  createVariant: (data: CreateVariantInput) =>
    api.post<ABVariant>('/lp-analytics/ab/variants', data),

  updateVariant: (id: string, data: { status: 'active' | 'inactive' }) =>
    api.patch<ABVariant>(`/lp-analytics/ab/variants/${id}`, data),

  getBridge: (days = 30) =>
    api.get<BridgeRow[]>(`/lp-analytics/bridge?days=${days}`),

  getPostHogStats: (start: string, end: string, site: SiteFilter = 'all') =>
    api.get<PostHogStats>(
      `/lp-analytics/posthog-stats?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}` +
      (site !== 'all' ? `&site=${site}` : '')
    ),

  getPostHogLive: (minutes = 5, site: SiteFilter = 'all') =>
    api.get<PostHogLive>(
      `/lp-analytics/posthog-live?minutes=${minutes}${site !== 'all' ? `&site=${site}` : ''}`
    ),

  getInsights: () =>
    api.get<{ insights: LPInsight[] }>('/lp-analytics/insights'),

  generateInsight: (site: SiteFilter = 'all', days = 7) =>
    api.post<LPInsight>(
      `/lp-analytics/insights/generate?days=${days}${site !== 'all' ? `&site=${site}` : ''}`,
      {}
    ),

  dismissInsight: (id: number) =>
    api.post<{ id: number; status: string }>(`/lp-analytics/insights/${id}/dismiss`, {}),

  getBookings: (site?: 'leitkraft' | 'leitscope') =>
    api.get<{ bookings: LPBooking[] }>(`/lp-analytics/bookings${site ? `?site=${site}` : ''}`),

  getKanaele: () =>
    api.get<{ kanaele: UtmKanal[]; ziele: UtmZiel[] }>('/lp-analytics/kanaele'),

  getLinks: () =>
    api.get<{ links: LPLink[] }>('/lp-analytics/links'),

  createLink: (data: CreateLinkInput) =>
    api.post<LPLink>('/lp-analytics/links', data),

  updateLink: (id: number, data: { titel?: string; notiz?: string }) =>
    api.patch<LPLink>(`/lp-analytics/links/${id}`, data),
};
